'use client';

import './gift-catalog-core.scss';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ElementType, Suspense, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.js';
import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.js';
import {
  Prominence,
  Sizes,
  Text,
  Variants,
} from '@sola/ark-core/atoms/text/text.js';
import { Alert } from '@sola/ark-core/molecules/alert/alert.js';
import { ChipGroup } from '@sola/ark-core/molecules/chip-group/chip-group.js';
import { Form } from '@sola/ark-core/molecules/form/form.js';
import {
  Select,
  SelectItemProps,
} from '@sola/ark-core/molecules/select/select.js';
import { SliderInput } from '@sola/ark-core/molecules/slider-input/slider-input.js';
import { cn } from '@sola/ark-core/utils/helpers.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { generalConfig } from '@sola/config/lib/getters.js';
import { BlockProps, FundProps } from '@sola/schema/lib/schema.js';
import { extractArgsSearchResult } from '@sola/skippy-search-sdk/lib/logic.js';
import { useListCatalogQuery } from '@sola/skippy-search-sdk/lib/queries.js';
import { SearchResultProps } from '@sola/skippy-search-sdk/lib/schema.js';
import {
  TrackViewFundItemList,
  trackClick,
  trackSelectFundItem,
} from '@sola/tracking/components/datalayer.js';

import { GiftCatalogCard } from '../../../molecules/gift-catalog-card/gift-catalog-card';
import { HeaderGroup } from '../../../molecules/header-group/header-group';
import {
  GiftCatalogSortEnum,
  useGiftCatalogQueryParams,
} from '../../../utils/query-params';
import {
  GiftCatalogCoreCardsSkeleton,
  GiftCatalogCoreSkeleton,
} from './gift-catalog-core-skeleton';

const queryClient = new QueryClient();

const InternalGiftCatalogCore: React.FC<BlockProps> = ({ seoLevel = 2 }) => {
  const [isCategoryOpen, setIsCategoryOpen] = useState(true);
  const [isPriceOpen, setIsPriceOpen] = useState(true);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [allResults, setAllResults] = useState<SearchResultProps[]>([]);

  const { t } = useTranslation(['gift-catalog', 'skippy']);
  const { giftCatalogFilters, setGiftCatalogFilters } =
    useGiftCatalogQueryParams();

  const selectedCategoryFilters = (() => {
    return giftCatalogFilters.category;
  })();

  const selectedPromotion = giftCatalogFilters.promotion;

  const [priceRange, setPriceRange] = useState<[number, number]>([
    generalConfig.catalogFilterMin,
    generalConfig.catalogFilterMax,
  ]);

  useEffect(() => {
    const minPrice = giftCatalogFilters.priceMin;
    const maxPrice = giftCatalogFilters.priceMax;

    setPriceRange([
      minPrice ?? generalConfig.catalogFilterMin,
      maxPrice ?? generalConfig.catalogFilterMax,
    ]);
  }, [giftCatalogFilters.priceMin, giftCatalogFilters.priceMax]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    giftCatalogFilters.category,
    giftCatalogFilters.priceMin,
    giftCatalogFilters.priceMax,
    giftCatalogFilters.sortby,
  ]);

  const categoryLabels = t('gift-catalog:taxonomy.fundCategories', {
    returnObjects: true,
  }) as Record<string, string>;

  const sortOptions: SelectItemProps[] = [
    {
      value: GiftCatalogSortEnum.enum.mostNeeded,
      text: t('gift-catalog:sort.mostNeeded'),
    },
    {
      value: GiftCatalogSortEnum.enum.priceLowToHigh,
      text: t('gift-catalog:sort.priceLowToHigh'),
    },
    {
      value: GiftCatalogSortEnum.enum.priceHighToLow,
      text: t('gift-catalog:sort.priceHighToLow'),
    },
    {
      value: GiftCatalogSortEnum.enum.alphabeticalAZ,
      text: t('gift-catalog:sort.alphabeticalAZ'),
    },
    {
      value: GiftCatalogSortEnum.enum.alphabeticalZA,
      text: t('gift-catalog:sort.alphabeticalZA'),
    },
  ];

  const getSearchSort = (
    sortBy: GiftCatalogSortEnum,
  ): {
    sort?:
      | 'pageTitle.keyword'
      | 'fund.catalogAmount'
      | 'datePublished'
      | 'dateModified';
    sortDir?: 'asc' | 'desc';
    should?: string;
  } => {
    switch (sortBy) {
      case GiftCatalogSortEnum.enum.mostNeeded:
        return {
          should: 'mostNeeded',
        };
      case GiftCatalogSortEnum.enum.priceLowToHigh:
        return {
          sort: 'fund.catalogAmount' as const,
          sortDir: 'asc' as const,
        };
      case GiftCatalogSortEnum.enum.priceHighToLow:
        return {
          sort: 'fund.catalogAmount' as const,
          sortDir: 'desc' as const,
        };
      case GiftCatalogSortEnum.enum.alphabeticalAZ:
        return {
          sort: 'pageTitle.keyword' as const,
          sortDir: 'asc' as const,
        };
      case GiftCatalogSortEnum.enum.alphabeticalZA:
        return {
          sort: 'pageTitle.keyword' as const,
          sortDir: 'desc' as const,
        };
      default:
        return {
          sort: 'pageTitle.keyword' as const,
          sortDir: 'asc' as const,
        };
    }
  };

  const searchInput = useMemo(() => {
    const filters: Record<string, string> = {};
    const should: Record<string, string> = {};
    const terms: Record<string, string[]> = {};
    const sortConfig = getSearchSort(
      giftCatalogFilters.sortby as GiftCatalogSortEnum,
    );

    if (selectedCategoryFilters.length > 0) {
      terms['taxonomy.fundCategory.keyword'] = selectedCategoryFilters;
    }

    // TODO: I don't think selectedPromotion is used right now so it will always fall back to sortConfig
    // If we ever have more options here on GC, we'll have to revisit this query
    const promotionFilter = selectedPromotion || sortConfig.should;
    if (promotionFilter) {
      should['taxonomy.fundPromotion.keyword'] = promotionFilter;
    }

    const ranges: Record<string, { lte: number; gte: number }> = {};

    const minPrice =
      giftCatalogFilters.priceMin ?? generalConfig.catalogFilterMin;
    const maxPrice =
      giftCatalogFilters.priceMax ?? generalConfig.catalogFilterMax;

    ranges['fund.catalogAmount'] = {
      gte: minPrice,
      lte: maxPrice,
    };

    return {
      offset: (currentPage - 1) * generalConfig.catalogPerPage,
      perPage: generalConfig.catalogPerPage,
      filters: JSON.stringify(filters),
      terms: JSON.stringify(terms),
      ranges: JSON.stringify(ranges),
      should: JSON.stringify(should),
      shouldStrictness: 0,
      sort: sortConfig.sort,
      sortDir: sortConfig.sortDir,
    };
  }, [
    currentPage,
    selectedCategoryFilters,
    selectedPromotion,
    giftCatalogFilters.priceMin,
    giftCatalogFilters.priceMax,
    giftCatalogFilters.sortby,
  ]);

  const {
    data: searchResults,
    isLoading,
    error,
  } = useListCatalogQuery(searchInput);

  const catalogFacetQuery = useListCatalogQuery({
    perPage: 1,
  });

  const { data: categoryData } = catalogFacetQuery;

  const facetCategories =
    categoryData?.facets.filter(
      (facet) => facet?.field === 'taxonomy.fundCategory.keyword',
    ) || [];

  const categories = (
    facetCategories[0]?.values.map((category) => ({
      name: t(`gift-catalog:taxonomy.fundCategories.${category.key}`),
      value: category.key,
    })) || []
  ).sort((a, b) => {
    const categoryA = a.name.toLowerCase();
    const categoryB = b.name.toLowerCase();
    if (categoryA < categoryB) {
      return -1;
    }
    if (categoryA > categoryB) {
      return 1;
    }
    return 0;
  });

  const handlePriceChange = (sliderRange: { newValue: number[] }) => {
    const newMin = sliderRange.newValue[0];
    const newMax = sliderRange.newValue[1];

    setGiftCatalogFilters((prev) => ({
      ...prev,
      priceMin: newMin !== generalConfig.catalogFilterMin ? newMin : null,
      priceMax: newMax !== generalConfig.catalogFilterMax ? newMax : null,
    }));
  };

  const handleActiveFilterChange = (selected: string[]) => {
    setGiftCatalogFilters((prev) => ({
      ...prev,
      category: selected,
    }));
  };

  const handleSortChange = (newSort: GiftCatalogSortEnum) => {
    setGiftCatalogFilters((prev) => ({
      ...prev,
      sortby: newSort,
    }));
  };

  useEffect(() => {
    if (!searchResults?.items) {
      return;
    }

    const newResults = searchResults.items.map(extractArgsSearchResult);

    if (currentPage === 1) {
      setAllResults(newResults);
    } else {
      setAllResults((prev) => [...prev, ...newResults]);
    }
  }, [searchResults, currentPage]);

  const Comp2 = `h${seoLevel + 1}` as ElementType;
  const Comp3 = `h${seoLevel + 2}` as ElementType;

  const hasMore =
    searchResults?.totalCount &&
    searchResults.totalCount > currentPage * generalConfig.catalogPerPage;

  const handleLoadMore = () => {
    setCurrentPage((prev) => prev + 1);
  };

  const resetAllFilters = () => {
    trackClick(t('gift-catalog:clearAll'));
    setGiftCatalogFilters(null);
  };

  const resetPriceFilter = () => {
    trackClick(t('gift-catalog:clearPriceRange'));
    // @TODO: Should the keys come from query-params so it's not hardcoded?
    setGiftCatalogFilters((prev) => ({
      ...prev,
      priceMin: null,
      priceMax: null,
    }));
  };

  const isPriceFiltered =
    (giftCatalogFilters.priceMin !== null &&
      giftCatalogFilters.priceMin !== generalConfig.catalogFilterMin) ||
    (giftCatalogFilters.priceMax !== null &&
      giftCatalogFilters.priceMax !== generalConfig.catalogFilterMax);

  // @TODO: Should we be using <Alert /> here?
  if (error) {
    return (
      <section className="GiftCatalogCore component">
        <div className="container">
          <Alert variant="negative" message={t('gift-catalog:error')} />
        </div>
      </section>
    );
  }

  return (
    <section className="GiftCatalogCore component">
      <div className="GiftCatalogCore__container container grid gap-comfy-sm md:gap-comfy-xl">
        <div className="GiftCatalogCore__header flex flex-col gap-comfy-md md:items-end md:flex-row ">
          <div className="flex-1 ">
            <HeaderGroup
              seoLevel={seoLevel}
              title={t('gift-catalog:viewAllGifts')}
              body={<p>{t('gift-catalog:subHeading')}</p>}
            />
          </div>

          <div className="flex gap-comfy-sm">
            <div className="GiftCatalogCore__filterToggle md:hidden flex-1">
              <ButtonStyle
                variant="primary"
                size="md"
                iconAfter={<ArkIcon iconName="settings-04" />}
              >
                <button
                  type="button"
                  className="justify-between w-full"
                  onClick={() => setIsMobileFiltersOpen(!isMobileFiltersOpen)}
                >
                  {selectedCategoryFilters.length > 0
                    ? t('gift-catalog:filterBy', {
                        count: selectedCategoryFilters.length,
                      })
                    : t('gift-catalog:filterByWithoutCount')}
                </button>
              </ButtonStyle>
            </div>
            <div className="GiftCatalogCore__sort grid gap-snug-lg relative flex-1">
              <div className="hidden md:block w-full">
                <Text variant={Variants.Label} size={Sizes.Medium} asChild>
                  <Comp2>{t('gift-catalog:sortBy')}</Comp2>
                </Text>
              </div>
              <Select
                name="sort"
                items={sortOptions}
                defaultValue={giftCatalogFilters.sortby}
                onChange={(e) => handleSortChange(e.target.value)}
                aria-label={t('gift-catalog:sortGiftsBy')}
              />
            </div>
          </div>
        </div>

        <div className="GiftCatalogCore__body grid items-start gap-comfy-lg">
          <aside
            className={cn(
              'GiftCatalogCore__filters grid px-comfy-sm py-comfy-lg rounded-lg',
              'md:grid',
              {
                hidden: !isMobileFiltersOpen,
                grid: isMobileFiltersOpen,
              },
            )}
          >
            <div className="GiftCatalogCore__filtersHeader flex items-center justify-between">
              <Text variant={Variants.Heading} size={Sizes.XSmall} asChild>
                <Comp2>
                  {selectedCategoryFilters.length > 0
                    ? t('gift-catalog:filterBy', {
                        count: selectedCategoryFilters.length,
                      })
                    : t('gift-catalog:filterByWithoutCount')}
                </Comp2>
              </Text>
              <LinkStyle size="lg" weightOverride="normal" noPadding>
                <button type="button" onClick={resetAllFilters}>
                  {t('gift-catalog:clearAll')}
                </button>
              </LinkStyle>
            </div>

            <div className="GiftCatalogCore__filterSection grid">
              <ButtonStyle
                variant="secondary"
                iconAfter={
                  isCategoryOpen ? (
                    <ArkIcon iconName="chevron-up" />
                  ) : (
                    <ArkIcon iconName="chevron-down" />
                  )
                }
              >
                <button
                  type="button"
                  onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                  className={`GiftCatalogCore__filterButton justify-between -mx-comfy-xs mt-comfy-xs mb-comfy-xs ${
                    !isCategoryOpen ? 'is-collapsed' : ''
                  }`}
                >
                  <Text
                    variant={Variants.Label}
                    size={Sizes.Large}
                    prominence={Prominence.Potent}
                    asChild
                  >
                    <Comp3>{t('gift-catalog:category')}</Comp3>
                  </Text>
                </button>
              </ButtonStyle>

              {isCategoryOpen && (
                <div className="GiftCatalogCore__filterBody pb-comfy-sm">
                  <ChipGroup
                    chips={categories}
                    handleSelectionChange={handleActiveFilterChange}
                    className="gap-snug-xl"
                    selectedValues={selectedCategoryFilters}
                    chipSize="md"
                  />
                </div>
              )}
            </div>
            <hr className="GiftCatalogCore__divider block m-none" />
            <div className="GiftCatalogCore__filterSection grid">
              <ButtonStyle
                variant="secondary"
                iconAfter={
                  isPriceOpen ? (
                    <ArkIcon iconName="chevron-up" />
                  ) : (
                    <ArkIcon iconName="chevron-down" />
                  )
                }
              >
                <button
                  type="button"
                  onClick={() => setIsPriceOpen(!isPriceOpen)}
                  className={`GiftCatalogCore__filterButton justify-between -mx-comfy-xs mt-comfy-xs mb-comfy-xs ${
                    !isPriceOpen ? 'is-collapsed -mb-comfy-xs' : ''
                  }`}
                >
                  <Text
                    variant={Variants.Label}
                    size={Sizes.Large}
                    prominence={Prominence.Potent}
                    asChild
                  >
                    <Comp3>{t('gift-catalog:priceRange')}</Comp3>
                  </Text>
                </button>
              </ButtonStyle>
              {isPriceOpen && (
                <div className="GiftCatalogCore__filterBody pb-comfy-sm">
                  <Form onSubmit={() => {}} className="grid gap-snug-md">
                    <SliderInput
                      name="priceRange"
                      min={generalConfig.catalogFilterMin}
                      max={generalConfig.catalogFilterMax}
                      step={10}
                      prefix="$"
                      showInputForm
                      value={priceRange}
                      onCommit={handlePriceChange}
                    />
                  </Form>
                </div>
              )}
            </div>
          </aside>

          <div
            className={cn('GiftCatalogCore__main grid gap-comfy-lg', {
              'pt-comfy-xs': !isMobileFiltersOpen,
            })}
          >
            {(selectedCategoryFilters.length > 0 || isPriceFiltered) && (
              <div className="flex flex-wrap gap-snug-xl">
                {selectedCategoryFilters.length > 0 && (
                  <ChipGroup
                    chips={selectedCategoryFilters.map((key) => ({
                      name: categoryLabels[key] || key,
                      value: key,
                      isSelected: false,
                    }))}
                    selectedValues={selectedCategoryFilters}
                    handleSelectionChange={handleActiveFilterChange}
                    removable
                    onRemoveChip={(value) =>
                      handleActiveFilterChange(
                        selectedCategoryFilters.filter((v) => v !== value),
                      )
                    }
                    className="gap-snug-xl"
                    chipSize="sm"
                  />
                )}
                {isPriceFiltered && (
                  <ChipGroup
                    chips={[
                      {
                        name: t('gift-catalog:priceRangeChip', {
                          min:
                            giftCatalogFilters.priceMin ??
                            generalConfig.catalogFilterMin,
                          max:
                            giftCatalogFilters.priceMax ??
                            generalConfig.catalogFilterMax,
                        }),
                        value: 'price-range',
                        isSelected: false,
                      },
                    ]}
                    selectedValues={[]}
                    handleSelectionChange={resetPriceFilter}
                    removable
                    onRemoveChip={resetPriceFilter}
                    className="gap-snug-xl"
                    chipSize="sm"
                  />
                )}
              </div>
            )}
            {isLoading && currentPage === 1 ? (
              <GiftCatalogCoreCardsSkeleton
                itemCount={generalConfig.catalogPerPage}
              />
            ) : (
              <>
                <Text variant={Variants.Label} size={Sizes.XLarge} asChild>
                  <Comp2>
                    {t('gift-catalog:showingGifts', {
                      count: allResults.length,
                      countAllGifts: searchResults?.totalCount,
                    })}
                  </Comp2>
                </Text>

                <div className="grid gap-comfy-sm @md:gap-comfy-lg grid-cols-1 @sm:grid-cols-2 @md:grid-cols-3">
                  {allResults && (
                    <TrackViewFundItemList
                      listId="gift_catalog"
                      listName="Gift Catalog"
                      items={allResults.map(
                        (result) => result.fund as FundProps,
                      )}
                      isCatalog
                    />
                  )}
                  {allResults.map((gift, index) => (
                    <GiftCatalogCard
                      seoLevel={seoLevel + 2}
                      key={gift.path || `gift-${index}`}
                      {...gift}
                      variant="grid"
                      onClick={() =>
                        trackSelectFundItem(
                          'giftCatalog',
                          'Gift Catalog',
                          gift.fund as FundProps,
                          true,
                        )
                      }
                    />
                  ))}
                </div>
              </>
            )}

            {Boolean(hasMore) && (
              <div className="flex justify-center mt-comfy-lg">
                <ButtonStyle variant="secondary" size="lg">
                  <button
                    type="button"
                    onClick={handleLoadMore}
                    disabled={isLoading}
                  >
                    {isLoading && currentPage > 1
                      ? t('gift-catalog:loading')
                      : t('gift-catalog:loadMore')}
                  </button>
                </ButtonStyle>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export const GiftCatalogCore: React.FC<BlockProps> = (props) => {
  return (
    <Suspense
      fallback={
        <GiftCatalogCoreSkeleton itemCount={generalConfig.catalogPerPage} />
      }
    >
      <QueryClientProvider client={queryClient}>
        <InternalGiftCatalogCore {...props} />
      </QueryClientProvider>
    </Suspense>
  );
};
